import { gql } from "@apollo/client";
import { Formik, FormikHelpers, Form } from "formik";
import { useSnackbar } from "notistack";
import * as yup from "yup";

import { Container } from "@material-ui/core";

import { useAdmin } from "src/components/LoggedInAdminProvider";
import PageTitle from "src/components/PageTitle";
import FormikTextField from "src/components/form/FormikTextField";
import SubmitButton from "src/components/form/SubmitButton";
import {
  adminEditOwnName,
  adminEditOwnNameVariables
} from "src/graphql/generated/adminEditOwnName";
import useMutation from "src/util/useMutation";

type Values = { fullName: string };

const schema = yup.object().shape({
  fullName: yup.string().required("Required")
});

export const UPDATE_OWN_NAME = gql`
  mutation adminEditOwnName($fullName: String!) {
    adminEditOwnName(newFullName: $fullName) {
      id
      fullName
    }
  }
`;

export default function Profile(): JSX.Element {
  const admin = useAdmin()!;
  const snackbar = useSnackbar();

  const [updateOwnName] = useMutation<
    adminEditOwnName,
    adminEditOwnNameVariables
  >(UPDATE_OWN_NAME, {
    onErrorSnackbarMessage: "Failed to update profile."
  });

  async function handleSubmit(values: Values, helpers: FormikHelpers<Values>) {
    const { errors, data } = await updateOwnName({
      variables: values
    });

    if (!errors) {
      snackbar.enqueueSnackbar("Profile updated.", { variant: "info" });
      helpers.resetForm({
        values: {
          fullName: data!.adminEditOwnName.fullName || ""
        }
      });
    }
  }

  return (
    <Container>
      <PageTitle title="Profile" />
      <p>
        <strong>Email: </strong>
        {admin.email}
      </p>
      <Formik
        initialValues={{
          fullName: admin.fullName || ""
        }}
        onSubmit={handleSubmit}
        validationSchema={schema}
      >
        {({ isSubmitting, dirty }) => (
          <Form>
            <FormikTextField
              fullWidth
              label="Full Name"
              name="fullName"
              disabled={isSubmitting}
            />
            <SubmitButton
              type="submit"
              disabled={!dirty}
              isSubmitting={isSubmitting}
              submitText="Update"
              loadingText="Updating..."
            />
          </Form>
        )}
      </Formik>
    </Container>
  );
}
